import {simpleTreatmentLabel,simpleReasonLabel,type SimpleProfitResolution} from './simple-profit-loss.ts';
import type { Transaction } from "./finance.ts";
import type { FinanceCategory } from "./categories.ts";
import { actualBrands, type BusinessUnit } from "./business-units.ts";
import type { FinancialAccount, ResolvedFinancialAccount } from "./profit-loss.ts";

type SearchMetadata = {
  categories: FinanceCategory[];
  brands: BusinessUnit[];
  accounts: FinancialAccount[];
  sources?: { id: string; filename: string }[];
  resolveSimpleProfit?:(row:Transaction)=>SimpleProfitResolution;
  resolveFinancialAccount?: (row:Transaction)=>ResolvedFinancialAccount;
};

export type SearchTerm = { text: string; amount?: string };

export function normalizeSearchText(value: unknown) {
  return String(value ?? "").normalize("NFKD").replace(/\p{M}/gu, "")
    .toLocaleLowerCase().replace(/\s+/g, " ").trim();
}

// Currency notation is normalized independently from reference numbers/codes.
// A numeric term may match part of amount digits OR occur in a textual field.
function amountTerm(value: string): string | undefined {
  const raw = value.replace(/^(?:rp|idr)\s*/i, "").trim();
  if (!/^\d[\d.,]*$/.test(raw)) return undefined;
  const integer = raw.replace(/[.,]00$/, "");
  return String(Number(integer.replace(/[.,]/g, "")));
}

export function parseTransactionSearch(query: string): SearchTerm[] {
  const normalized = normalizeSearchText(query)
    .replace(/\b(?:rp|idr)\s+(?=\d)/g, "rp");
  return (normalized.match(/"[^"]+"|\S+/g) || []).map((token) => {
    const text = token.replace(/^"|"$/g, "");
    return { text, amount: amountTerm(text) };
  });
}

function dateLabels(date: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const [year, month, day] = date.split("-");
  const value = new Date(`${date}T00:00:00Z`);
  return [date, `${day}/${month}/${year}`, `${day}-${month}-${year}`,
    ...dateFormatters.map((formatter) => formatter.format(value))].join(" ");
}

const dateFormatters = ["id-ID", "en-US"].map((locale) => new Intl.DateTimeFormat(locale, {
  day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
}));

/** Reuse unchanged row documents across edits and filter changes. No learning. */
export function createTransactionSearchIndex(metadata: SearchMetadata) {
  const categories = new Map(metadata.categories.map((value) => [value.slug, `${value.nameId} ${value.nameEn}`]));
  const brands = new Map(metadata.brands.map((value) => [value.slug, `${value.name} ${value.shortName}`]));
  const accounts = new Map(metadata.accounts.map((value) => [value.slug, `${value.nameId} ${value.nameEn}`]));
  const sources = new Map(metadata.sources?.map((value) => [value.id, value.filename]));
  const cache = new WeakMap<Transaction, { text: string; amounts: string[] }>();
  const dates = new Map<string, string>();
  return {
    matches(row: Transaction, terms: SearchTerm[]) {
      if (!terms.length) return true;
      let document = cache.get(row);
      if (!document) {
        let date = dates.get(row.date);
        if (date === undefined) { date = dateLabels(row.date); dates.set(row.date, date); }
        const actual = actualBrands(row);
        const financial=metadata.resolveFinancialAccount?.(row);
        const simple=metadata.resolveSimpleProfit?.(row);
        document = {
          text: normalizeSearchText([
            row.paymentRef, row.ftNumber, row.sourceName, row.sourceBank, row.sourceAccount,
            row.beneficiaryName, row.beneficiaryBank, row.beneficiaryAccount, row.accountLabel,
            row.subject, row.internalDescription, row.transferType, row.uniqueCode,
            row.category, categories.get(row.category), row.subcategory, categories.get(row.subcategory || ""), row.categoryRuleConflict ? "konflik aturan rule conflict" : "",
            actual.length ? actual.map((slug) => `${slug} ${brands.get(slug) || ""}`).join(" ") : "belum ditentukan unassigned",
            actual.length > 1 ? "kolaborasi collaboration" : "",
            row.financialAccount, accounts.get(row.financialAccount || ""), financial?.account?.slug, financial?.account?.nameId, financial?.account?.nameEn,
            simple&&simpleTreatmentLabel(simple.treatment,"id"),simple&&simpleTreatmentLabel(simple.treatment,"en"),simple&&simpleReasonLabel(simple.reason,"id"),simple&&simpleReasonLabel(simple.reason,"en"),
            row.sourceFile, ...row.sourceFiles, ...row.sourceBatchIds.map((id) => sources.get(id)),
            date, row.transactionTimestamp?.replace("T", " "),
            row.entryMethod === "manual" ? "input manual" : "impor import",
            row.direction === "CREDIT" ? "kredit credit pemasukan income" : row.direction === "DEBIT" ? "debet debit pengeluaran expense" : "",
          ].filter(Boolean).join(" ")),
          amounts: [row.amount, row.balance].filter((value): value is number => value !== undefined && Number.isFinite(value)).map(String),
        };
        cache.set(row, document);
      }
      // Every term must match somewhere; terms may match different fields.
      return terms.every((term) => document.text.includes(term.text)
        || (term.amount !== undefined && document.amounts.some(amount => amount.includes(term.amount!))));
    },
  };
}
